import { useState } from 'react';
import { CalendarDays, Check, Copy } from 'lucide-react';
import type { TaskFeedLink, User } from '@peles/shared';
import { useDialog } from '../../context/DialogContext';
import { useCreateTaskFeed, useDeleteTaskFeed, useTaskFeed } from '../../hooks/useTaskFeed';

function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const buttonClass =
  'px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50';

/**
 * A person's tasks in their own calendar: a private address Google Calendar,
 * Apple Calendar or Outlook subscribes to.
 *
 * ⚠️ **The address is shown once.** Only its hash is kept, so after this
 * screen it cannot be shown again — "New address" makes another and ends the
 * old one, which is also what to do if it was shared by mistake.
 */
export function TaskCalendarFeedSetting({ user }: { user: User }) {
  const status = useTaskFeed(user.id);
  const create = useCreateTaskFeed(user.id);
  const remove = useDeleteTaskFeed(user.id);
  const { confirm } = useDialog();
  const [link, setLink] = useState<TaskFeedLink | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const busy = create.isPending || remove.isPending;

  async function makeAddress(replacing: boolean) {
    if (
      replacing &&
      !(await confirm({
        title: 'Make a new address?',
        message:
          'The current address stops working at once. Any calendar using it will need the new one.',
        confirmLabel: 'Make a new address',
        variant: 'warning',
      }))
    ) {
      return;
    }
    setError(null);
    setCopied(false);
    create.mutate(undefined, {
      onSuccess: setLink,
      onError: () => setError('The address could not be made. Try again.'),
    });
  }

  async function turnOff() {
    if (
      !(await confirm({
        title: 'Turn off your task calendar?',
        message: 'Calendars subscribed to it stop receiving your tasks.',
        confirmLabel: 'Turn off',
      }))
    ) {
      return;
    }
    setError(null);
    remove.mutate(undefined, {
      onSuccess: () => setLink(null),
      onError: () => setError('It could not be turned off. Try again.'),
    });
  }

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setError('Copying is blocked here — select the address and copy it by hand.');
    }
  }

  const feed = status.data;

  return (
    <div className="bg-white rounded-lg shadow p-6" data-testid="task-calendar-feed">
      <h3 className="flex items-center gap-2 text-sm font-medium text-gray-900">
        <CalendarDays className="h-4 w-4 text-gray-500" aria-hidden="true" />
        Tasks in your calendar
      </h3>
      <p className="text-sm text-gray-500 mt-0.5">
        Your open tasks with a due date, each on its due day, in Google Calendar, Apple Calendar or
        Outlook. Calendars refresh it on their own schedule — Google can take several hours.
      </p>

      {link ? (
        <div className="mt-3 space-y-2">
          <div className="flex gap-2">
            <input
              readOnly
              value={link.url}
              aria-label="Your task calendar address"
              onFocus={(e) => e.target.select()}
              className="flex-1 min-w-0 px-3 py-1.5 border border-gray-300 rounded-md text-xs font-mono text-gray-700 bg-gray-50"
            />
            <button type="button" onClick={() => void copy(link.url)} className={buttonClass}>
              <span className="inline-flex items-center gap-1">
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? 'Copied' : 'Copy'}
              </span>
            </button>
          </div>
          <ul className="text-xs text-gray-600 list-disc pl-5 space-y-0.5">
            <li>Google Calendar: Other calendars → + → From URL, and paste the address.</li>
            <li>
              Apple Calendar or Outlook:{' '}
              <a href={link.webcalUrl} className="text-primary-700 hover:underline">
                subscribe
              </a>
              , or add a calendar from the internet and paste it.
            </li>
          </ul>
          <p className="text-xs text-amber-700">
            This address is shown only now, and anyone who has it can see your tasks — keep it to
            yourself.
          </p>
          <button type="button" onClick={() => setLink(null)} className={buttonClass}>
            Done
          </button>
        </div>
      ) : status.isLoading ? (
        <p className="mt-3 text-sm text-gray-400">Loading…</p>
      ) : status.isError ? (
        <p className="mt-3 text-sm text-red-700">Your calendar setting could not be loaded.</p>
      ) : feed?.enabled ? (
        <div className="mt-3 space-y-2">
          <p className="text-sm text-gray-700">
            On since {feed.createdAt ? formatDay(feed.createdAt) : '—'} ·{' '}
            {feed.lastFetchedAt
              ? `last read by a calendar ${formatDay(feed.lastFetchedAt)}`
              : 'not read by a calendar yet'}
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => void makeAddress(true)}
              className={buttonClass}
            >
              New address
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void turnOff()}
              className="px-3 py-1.5 text-sm rounded-md text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              Turn off
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          disabled={busy}
          onClick={() => void makeAddress(false)}
          className="mt-3 px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {create.isPending ? 'Making…' : 'Get my calendar address'}
        </button>
      )}

      {error && (
        <p role="alert" className="mt-2 text-sm text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}
